import React from 'react';
import PropTypes from 'prop-types';
import SelectHTMLSelect from './SelectHTMLSelect';
import SelectOptions from './SelectOptions';
import './Select.scss';

class SelectContainer extends React.Component {
  constructor(props) {
    super(props);
    this.selectRef = React.createRef();
    this.optionsWrapperRef = React.createRef();
    this.arrayOfOptionsRefs = [];
    this.state = {
      currentOption:
        props.startingValue || props.defaultOption || props.emptyValueOption,
      openOptions: false,
      focusedOption: undefined,
      hasBeenChanged: false
    };

    if (props.startingValue) {
      this.state.hasBeenChanged = props.startingValue !== props.defaultOption;
    }

    if (props.clearCommand) {
      props.clearCommand.invoke = () => this.handleClearValue();
    }
  }

  isDefaultOption(option) {
    return (
      option === this.props.defaultOption ||
      option === this.props.emptyValueOption
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.openOptions !== this.state.openOptions) {
      if (!this.state.openOptions) {
        this.selectRef.current.focus();
      } else {
        if (!this.arrayOfOptionsRefs || !this.arrayOfOptionsRefs.length) {
          return;
        }
        this.arrayOfOptionsRefs[0].focus();
      }
    }

    if (prevProps.defaultOption !== this.props.defaultOption) {
      if (this.state.currentOption === prevProps.defaultOption) {
        this.setState({
          currentOption: this.props.defaultOption || this.props.emptyValueOption
        });
      }
    }

    if (prevProps.startingValue !== this.props.startingValue) {
      if (this.props.startingValue) {
        this.setState({
          currentOption: this.props.startingValue,
          hasBeenChanged: this.props.startingValue !== this.props.defaultOption
        });
      }
    }

    if (prevProps.optionList !== this.props.optionList) {
      if (
        !(this.props.optionList && this.props.optionList.length) &&
        !this.isDefaultOption(this.state.currentOption)
      ) {
        this.props.controlFunc({
          valueId: this.props.formValue.valueId,
          value: ''
        });
        this.setState({
          currentOption:
            this.props.defaultOption || this.props.emptyValueOption,
          focusedOption: undefined,
          hasBeenChanged: false
        });
      }
    }

    if (prevProps.disabled !== this.props.disabled) {
      if (this.props.disabled) {
        this.setState({
          openOptions: false
        });
      }
    }
  }

  clearOptionsRefs = () => {
    this.arrayOfOptionsRefs = [];
  };

  handleSubmit = () => {
    this.props.controlFunc({
      valueId: this.props.formValue.valueId,
      value: this.state.currentOption || this.props.optionList[0]
    });
  };

  handleOpenOptions = event => {
    if (this.state.openOptions) {
      this.setState({
        openOptions: false,
        searchString: ''
      });
      return;
    }

    switch (event.type) {
      case 'click':
        this._handleOpenOptions(event);
        break;

      case 'keydown':
        const selectedIndex =
          this.props.optionList.findIndex(
            option => option === this.state.currentOption
          ) || 0;

        if (event.key === ' ') {
          event.preventDefault();
          this._handleOpenOptions(event);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          const prevOption = this.props.optionList[
            Math.max(0, selectedIndex - 1)
          ];
          this.setState(
            () => ({
              currentOption: prevOption,
              hasBeenChanged: true
            }),
            () => {
              this.handleSubmit();
            }
          );
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          const nextOption = this.props.optionList[
            Math.min(this.props.optionList.length - 1, selectedIndex + 1)
          ];
          this.setState(
            () => ({
              currentOption: nextOption,
              hasBeenChanged: true
            }),
            () => {
              this.handleSubmit();
            }
          );
        } else if (event.key.match(/^[\d\w]$/i)) {
          event.preventDefault();

          const { searchString } = this.state;

          let matchingOption;

          if (
            searchString &&
            searchString.length === 1 &&
            event.key.toLowerCase() === searchString.toLowerCase()
          ) {
            matchingOption =
              this.props.optionList.find((option, index) => {
                return (
                  ('' + option)
                    .toLowerCase()
                    .indexOf(searchString.toLowerCase()) === 0 &&
                  index > selectedIndex
                );
              }) ||
              this.props.optionList.find(option => {
                return (
                  ('' + option)
                    .toLowerCase()
                    .indexOf(searchString.toLowerCase()) === 0
                );
              });
          } else {
            const newSearchString = (searchString || '') + event.key;
            this.setState(() => ({
              searchString: newSearchString
            }));

            matchingOption = this.props.optionList.find(
              option =>
                ('' + option)
                  .toLowerCase()
                  .indexOf(newSearchString.toLowerCase()) === 0
            );
          }

          if (matchingOption) {
            this.setState(
              () => ({
                currentOption: matchingOption,
                hasBeenChanged: true
              }),
              () => {
                this.handleSubmit();
              }
            );
          } else {
            this.setState(() => ({
              searchString: ''
            }));
          }

          if (this.state.searchTimeout) {
            clearTimeout(this.state.searchTimeout);
          }

          this.setState(() => ({
            searchTimeout: setTimeout(() => {
              this.setState(() => ({
                searchString: '',
                searchTimeout: undefined
              }));
            }, 1800)
          }));
        }

        break;
      default:
    }
  };

  _handleOpenOptions = event => {
    this.setState(state => {
      let focusedOption = document.activeElement.id;
      return {
        openOptions: !state.openOptions,
        focusedOption: focusedOption
      };
    });
  };

  handleOptionsEvents = (value, index, event) => {
    switch (event.type) {
      case 'click':
        this.setState(
          () => ({
            currentOption: value,
            openOptions: !this.state.openOptions,
            hasBeenChanged: true
          }),
          () => {
            this.handleSubmit();
          }
        );
        this.selectRef.current.focus();
        break;
      case 'keydown':
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          this.setState(
            () => ({
              currentOption: value,
              openOptions: !this.state.openOptions,
              hasBeenChanged: true
            }),
            () => {
              this.handleSubmit();
            }
          );
          this.selectRef.current.focus();
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (!this.arrayOfOptionsRefs || !this.arrayOfOptionsRefs.length) {
            return;
          }
          this.arrayOfOptionsRefs[Math.max(0, index - 1)].focus();
          this.setState(() => ({
            focusedOption: document.activeElement.id
          }));
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (!this.arrayOfOptionsRefs || !this.arrayOfOptionsRefs.length) {
            return;
          }
          this.arrayOfOptionsRefs[
            Math.min(this.props.optionList.length - 1, index + 1)
          ].focus();
          this.setState(() => ({
            focusedOption: document.activeElement.id
          }));
        }
        if (String.fromCharCode(event.keyCode).match(/(\w|\s)/g)) {
          event.preventDefault();

          const { searchString } = this.state;

          let matchingOption;
          let newSearchString = searchString || '';

          if (
            newSearchString &&
            newSearchString.length === 1 &&
            event.key.toLowerCase() === newSearchString.toLowerCase()
          ) {
            matchingOption = this.props.optionList.find((option, loopIndex) => {
              return (
                ('' + option)
                  .toLowerCase()
                  .indexOf(newSearchString.toLowerCase()) === 0 &&
                loopIndex > index
              );
            });
          } else {
            newSearchString = newSearchString + event.key;
            this.setState(() => ({
              searchString: newSearchString
            }));

            matchingOption = this.props.optionList.find(
              option =>
                ('' + option)
                  .toLowerCase()
                  .indexOf(newSearchString.toLowerCase()) === 0
            );
          }

          if (matchingOption) {
            const matchingOptionIndex = this.props.optionList.findIndex(
              option => option === matchingOption
            );

            try {
              this.optionsWrapperRef.current.scrollTop =
                this.arrayOfOptionsRefs[matchingOptionIndex].offsetTop - 17;
              this.arrayOfOptionsRefs[matchingOptionIndex].focus();
            } catch (error) {
              console.log('refs not set correctly in Select');
            }
            this.setState(() => ({
              focusedOption: matchingOption
            }));
          } else {
            this.setState(() => ({
              searchString: ''
            }));
          }

          if (this.state.searchTimeout) {
            clearTimeout(this.state.searchTimeout);
          }

          this.setState(() => ({
            searchTimeout: setTimeout(() => {
              this.setState(() => ({
                searchString: '',
                searchTimeout: undefined
              }));
            }, 1000)
          }));
        }
        if (event.key === 'Escape') {
          this.setState(
            () => {
              return { openOptions: !this.state.openOptions, searchString: '' };
            },
            () => {
              this.selectRef.current.focus();
            }
          );
        }
        break;
      default:
    }
  };

  setOptionRef = element => {
    if (element !== null) {
      this.arrayOfOptionsRefs.push(element);
    }
  };

  handleClearValue() {
    this.setState({
      currentOption: this.props.emptyValueOption || this.props.defaultOption,
      hasBeenChanged: false
    });
    this.props.controlFunc({
      valueId: this.props.formValue.valueId,
      value: ''
    });
  }

  render() {
    const {
      currentOption,
      openOptions,
      focusedOption,
      hasBeenChanged
    } = this.state;
    const {
      formValue,
      optionList,
      topList,
      topListHeading,
      hideLabel,
      containerStyle,
      emptyMessage,
      disabled,
      emptyValueOption,
      showClearButton,
      actionable,
      optional
    } = this.props;
    const { name, labelTitle } = formValue;
    return (
      <div className="select-wrapper">
        <label htmlFor={name} className={hideLabel ? 'visually-hidden ' : ''}>
          {labelTitle}
        </label>
        <div
          className={
            'select-container ' +
            (disabled ? 'disabled ' : '') +
            (actionable ? 'actionable ' : '') +
            (optional ? 'optional ' : '')
          }
          tabIndex={disabled ? '-1' : undefined}
          style={containerStyle}
        >
          <SelectHTMLSelect
            name={name}
            handleOpenOptions={this.handleOpenOptions}
            handleClearValue={() => this.handleClearValue()}
            openOptions={this.state.openOptions}
            selectRef={this.selectRef}
            currentOption={currentOption}
            hasBeenChanged={hasBeenChanged}
            disabled={disabled}
            actionable={actionable}
            emptyValueOption={emptyValueOption}
            showClearButton={showClearButton}
            optional={optional}
          />
          {openOptions === true ? (
            <SelectOptions
              handleOptionsEvents={this.handleOptionsEvents}
              optionsWrapperRef={this.optionsWrapperRef}
              setOptionRef={this.setOptionRef}
              currentOption={currentOption}
              focusedOption={focusedOption}
              optionList={optionList}
              topList={topList}
              topListHeading={topListHeading}
              emptyMessage={emptyMessage}
            />
          ) : (
            [this.clearOptionsRefs(), null]
          )}
        </div>
      </div>
    );
  }
}

SelectContainer.propTypes = {
  formValue: PropTypes.object.isRequired,
  controlFunc: PropTypes.func.isRequired,
  optionList: PropTypes.array.isRequired,
  defaultOption: PropTypes.string,
  startingValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideLabel: PropTypes.bool,
  containerStyle: PropTypes.object,
  emptyMessage: PropTypes.string,
  disabled: PropTypes.bool,
  emptyValueOption: PropTypes.string,
  showClearButton: PropTypes.bool,
  actionable: PropTypes.bool,
  optional: PropTypes.bool
};

SelectContainer.defaultProps = {
  defaultOption: '',
  hideLabel: false,
  emptyMessage: 'No available options',
  disabled: false,
  actionable: false,
  emptyValueOption: 'Select',
  showClearButton: true,
  optional: false
};

export default SelectContainer;
